/**
 * Shared Fuzzy Search Utilities
 *
 * Common fuzzy search functions used by FAQ and Blog search
 * Includes tokenization, fuzzy matching, and field scoring
 */

/**
 * Search Configuration
 */
export const SEARCH_CONFIG = {
  fuzzyThreshold: 2, // Max Levenshtein distance for fuzzy match
  minWordLength: 2, // Minimum word length for fuzzy matching
  requireAllWords: true, // AND logic (all words must match)
  caseSensitive: false, // Case-insensitive search
} as const;

/**
 * Token match information
 */
export interface TokenMatch {
  token: string;
  matchedText: string;
  isExact: boolean;
  isFuzzy: boolean;
  position: number;
  distance?: number;
}

/**
 * Calculate Levenshtein distance between two strings
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create matrix
  const matrix: number[][] = [];

  // Initialize first row and column
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1, // deletion
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j - 1] + 1 // substitution
        );
      }
    }
  }

  return matrix[len1][len2];
}

/**
 * Check if a string matches query with fuzzy tolerance
 */
export function fuzzyMatch(
  text: string,
  query: string,
  threshold: number = SEARCH_CONFIG.fuzzyThreshold
): boolean {
  if (!text || !query) return false;

  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();

  // Exact match (substring)
  if (textLower.includes(queryLower)) return true;

  // For short queries, check if query is similar to any word in text
  if (query.length <= threshold * 2) {
    const words = textLower.split(/\s+/);
    for (const word of words) {
      if (word.length >= SEARCH_CONFIG.minWordLength) {
        const distance = levenshteinDistance(word, queryLower);
        if (distance <= threshold) return true;
      }
    }
  }

  // Check if query is similar to text (for longer queries)
  if (Math.abs(text.length - query.length) <= threshold * 2) {
    const distance = levenshteinDistance(textLower, queryLower);
    if (distance <= threshold) return true;
  }

  return false;
}

/**
 * Tokenize a string into words
 */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[\s\p{P}]+/u)
    .filter((token) => token.length >= SEARCH_CONFIG.minWordLength);
}

/**
 * Find token matches in text (exact and fuzzy)
 */
export function findTokenMatches(text: string, queryTokens: string[]): TokenMatch[] {
  const textLower = text.toLowerCase();
  const matches: TokenMatch[] = [];

  for (const token of queryTokens) {
    // Try exact match first (as substring)
    const exactIndex = textLower.indexOf(token);
    if (exactIndex !== -1) {
      matches.push({
        token,
        matchedText: text.substring(exactIndex, exactIndex + token.length),
        isExact: true,
        isFuzzy: false,
        position: exactIndex,
      });
      continue;
    }

    // Try fuzzy match against individual words
    const words = textLower.split(/\W+/).filter((w) => w.length > 0);
    let bestMatch: { word: string; distance: number; index: number } | null = null;

    // Find the best fuzzy match
    let currentIndex = 0;
    for (const word of words) {
      if (word.length >= SEARCH_CONFIG.minWordLength) {
        const distance = levenshteinDistance(word, token);
        if (distance <= SEARCH_CONFIG.fuzzyThreshold) {
          if (!bestMatch || distance < bestMatch.distance) {
            // Find actual position of this word in original text
            const wordIndex = textLower.indexOf(word, currentIndex);
            bestMatch = {
              word,
              distance,
              index: wordIndex,
            };
          }
        }
      }
      // Update currentIndex to search from after this word
      const wordEndIndex = textLower.indexOf(word, currentIndex) + word.length;
      currentIndex = Math.max(currentIndex, wordEndIndex);
    }

    if (bestMatch) {
      const wordIndex = bestMatch.index;
      const matchedWord = text.substring(wordIndex, wordIndex + bestMatch.word.length);
      matches.push({
        token,
        matchedText: matchedWord,
        isExact: false,
        isFuzzy: true,
        position: wordIndex,
        distance: bestMatch.distance,
      });
    }
  }

  return matches;
}

/**
 * Calculate score for a field based on matches
 */
export function calculateFieldScore(matches: TokenMatch[], fieldText: string): number {
  if (matches.length === 0) return 0;

  let score = 0;
  const fieldLength = fieldText.length;

  for (const match of matches) {
    let matchScore = 0;

    if (match.isExact) {
      matchScore = 100;
    } else if (match.isFuzzy && match.distance !== undefined) {
      // Fuzzy match: 50 - (distance * 10)
      matchScore = Math.max(0, 50 - match.distance * 10);
    }

    // Word boundary bonus
    const beforeChar = fieldText[match.position - 1] || " ";
    const afterChar = fieldText[match.position + match.matchedText.length] || " ";
    if (/\s/.test(beforeChar) && /\s/.test(afterChar)) {
      matchScore += 10;
    }

    // Early position bonus
    const positionRatio = match.position / fieldLength;
    matchScore += Math.max(0, 5 - positionRatio * 5);

    score += matchScore;
  }

  return score;
}
